import React, { FC } from 'react';
import RadioIcon, { OptionButton } from '~Components/RadioOptions';
import { useStorage } from '~Components/Storage';
import Heading from '~ComponentsImproved/Typography/Heading';
import { useButton, useDispatch, useI18n } from '~Hooks';
import { Box } from '~Styles';
import { GetDevices_operativeViewer_devices } from '~Components/DeviceProvider/__queries__';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import Icon from '~ComponentsImproved/Icon';
import LockClockIcon from '~Assets/icons/lock_clock.svg';
import { useDeviceHandler } from '~Components/DeviceProvider';
import { DateTime } from 'luxon';
import { PRODUCT_TO_UI_COPY_MAP } from '~Providers/LicensesProvider/Components/ClaimLicense';
import * as actions from '~actions';

interface Props {
  isHandy?: boolean;
  device: GetDevices_operativeViewer_devices;
  isSelected: boolean;
  onSelect: (id: string) => void;
}

/** A device that can be selected to claim (if the device is already used by the same clientId it will be disabled) */
const SelectableDevice: FC<Props> = ({ isHandy = false, device, isSelected, onSelect }) => {
  const { i18n } = useI18n();

  const [storage] = useStorage();
  const deviceHandler = useDeviceHandler();
  const dispatch = useDispatch();

  /** Allow selection of all devices if isUsingBrowser is true */
  const isUsingBrowser = !window.ReactNativeWebView;

  const selectButton = useButton({
    onClick: () => {
      if (isUsedByThisDevice) {
        return;
      }

      if (device?.lastClaimedAt && hasClaimCooldown && !isUsingBrowser) {
        // Create a DateTime object directly from the lastClaimedAt ISO string
        const cooldownEndDate = DateTime.fromISO(device.lastClaimedAt, { zone: 'utc' })
          .plus({ days: 7 })
          .toFormat('d MMMM HH:mm');

        dispatch(
          actions.addToast(
            `${i18n('ClaimDevice.Select.DeviceInUse', device?.name)} ${cooldownEndDate}`,
            true,
          ),
        );
        return;
      }

      onSelect(device.id);
    },
  });

  /** Only licenses with a known product get a label, unknown/undefined ones are dropped and sorted by label */
  const licenseLabels = (device?.activeLicenses || [])
    .map((license) => PRODUCT_TO_UI_COPY_MAP[license.product])
    .filter((label): label is string => !!label)
    .sort((a, b) => a.localeCompare(b));

  /** "TSR, QSR & KDS" – comma separated with an ampersand before the last one */
  const licenseText =
    licenseLabels.length > 1
      ? `${licenseLabels.slice(0, -1).join(', ')} & ${licenseLabels[licenseLabels.length - 1]}`
      : licenseLabels.join('');

  /** Don't display devices if this is true since then it can be an old company with the old licenses setup */
  const hasAnyLicenseOnAnyDevice = deviceHandler.allDevices.some(
    (d) => d.activeLicenses && d.activeLicenses.length > 0,
  );

  const hasClaimCooldown = device?.lastClaimedAt
    ? DateTime.fromISO(device.lastClaimedAt, { zone: 'utc' })
        .plus({ days: 7 })
        .toJSDate() > DateTime.utc().toJSDate()
    : false;

  const isUsedByThisDevice = device?.id === storage.deviceId;

  return (
    <OptionButton
      selected={isSelected}
      compact={isHandy}
      {...selectButton}
      disabled={!isUsingBrowser && (hasClaimCooldown || isUsedByThisDevice)}>
      <RadioIcon active={isSelected} compact={isHandy} />
      <Box display="flex" alignItems="center" justifyContent="space-between" gap="small" fluid>
        <Box display="flex" flexDirection="column" style={{ minWidth: 0 }}>
          <Heading level={isHandy ? 6 : 4} bottom={0} color="var(--colorWhite)">
            {device.name}{' '}
            {device?.ownerClientId &&
              `(${
                isUsedByThisDevice
                  ? i18n('ClaimDevice.Select.InUseByThisDevice')
                  : i18n('ClaimDevice.Select.InUse')
              })`}
          </Heading>
          {hasAnyLicenseOnAnyDevice && (
            <Paragraph
              level={5}
              style={{ fontSize: isHandy ? '10px' : undefined }}
              bottom={0}
              color="var(--colorBlueGray200)">
              {i18n('ClaimDevice.Select.Licenses')}:{' '}
              {licenseLabels.length > 0 ? licenseText : i18n('ClaimDevice.Select.Licenses.None')}
            </Paragraph>
          )}
        </Box>

        {hasClaimCooldown && !isUsedByThisDevice && (
          /** flexShrink 0 so long license text never squeezes the icon */
          <Box style={{ flexShrink: 0 }}>
            <Icon
              height={isHandy ? '16px' : '24px'}
              width={isHandy ? '16px' : '24px'}
              src={LockClockIcon}
              color="var(--colorBlueGray200)"
            />
          </Box>
        )}
      </Box>
    </OptionButton>
  );
};

export default SelectableDevice;
